/**
 * Ana ekranın sol sütunu: hava durumu ve döviz kurları (18 Eki 2026).
 *
 * İkisi de anahtarsız, ücretsiz servisler:
 * - Hava: Open-Meteo (api.open-meteo.com). Konum şimdilik İstanbul; kurumun
 *   şehri tutulmuyor.
 * - Kur: anlık piyasa kuru, aşağıda fetchRates.
 *
 * Her açılışta servise gitmesin diye tarayıcıda saklanıyor (hava 15 dk, kur
 * 2 dk). Depolama ya da ağ yoksa sessizce boş dönüyor; ana ekran o alanı
 * göstermiyor.
 */

const ISTANBUL = { lat: 41.01, lon: 28.98, name: 'İstanbul' }

export interface Weather {
  place: string
  tempC: number
  code: number
  isDay: boolean
}

export interface Rate {
  code: 'USD' | 'EUR' | 'GBP'
  /** 1 birim kaç TL. */
  tl: number
  /** Bir önceki iş gününe göre yüzde değişim. */
  changePct: number
}

export interface Rates {
  /** Kurun güncellendiği an (ISO). */
  date: string
  list: Rate[]
  /** Nereden geldiği, ekranın altında yazıyor. */
  source: string
}

function cached<T>(key: string, maxAgeMs: number): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const { at, value } = JSON.parse(raw) as { at: number; value: T }
    return Date.now() - at < maxAgeMs ? value : null
  } catch {
    return null
  }
}

function store<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), value }))
  } catch {
    // Saklanamazsa bir sonraki açılışta yeniden istenir.
  }
}

export async function fetchWeather(): Promise<Weather | null> {
  const key = 'adissoo.home.weather'
  const hit = cached<Weather>(key, 15 * 60_000)
  if (hit) return hit
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${ISTANBUL.lat}&longitude=${ISTANBUL.lon}` +
      '&current=temperature_2m,weather_code,is_day&timezone=Europe%2FIstanbul'
    const res = await fetch(url)
    if (!res.ok) return null
    const j = (await res.json()) as { current?: { temperature_2m?: number; weather_code?: number; is_day?: number } }
    const c = j.current
    if (!c || typeof c.temperature_2m !== 'number') return null
    const w: Weather = {
      place: ISTANBUL.name,
      tempC: c.temperature_2m,
      code: c.weather_code ?? 0,
      isDay: c.is_day !== 0,
    }
    store(key, w)
    return w
  } catch {
    return null
  }
}

/**
 * Anlık kur (20 Eki 2026, kullanıcı: "günlük kuru anlık çeksin"). Eskiden
 * Avrupa Merkez Bankası'nın günde bir kez açıklanan kuruydu (hafta sonu hiç
 * değişmiyordu) ve 3 saat saklanıyordu.
 *
 * - Truncgil Finans (finans.truncgil.com): Türkiye piyasa kuru, dakikalar içinde
 *   güncelleniyor; satış ve günlük değişim yüzdesi. Anahtarsız, CORS açık.
 * - Cevap gelmezse Coinbase (api.coinbase.com): o da anlık, değişim vermiyor.
 *
 * 2 dakika saklanıyor; ana ekran açıkken dakikada bir yeniden soruluyor.
 */
export async function fetchRates(): Promise<Rates | null> {
  const key = 'adissoo.home.rates2'
  const hit = cached<Rates>(key, 2 * 60_000)
  if (hit) return hit
  const rates = (await fromTruncgil()) ?? (await fromCoinbase())
  if (rates) store(key, rates)
  return rates
}

const CODES = ['USD', 'EUR', 'GBP'] as const

async function fromTruncgil(): Promise<Rates | null> {
  try {
    const res = await fetch('https://finans.truncgil.com/v4/today.json')
    if (!res.ok) return null
    const j = (await res.json()) as Record<string, { Selling?: number; Change?: number } | string>
    const list: Rate[] = []
    for (const code of CODES) {
      const r = j[code]
      if (typeof r !== 'object' || typeof r.Selling !== 'number') return null
      list.push({ code, tl: r.Selling, changePct: typeof r.Change === 'number' ? r.Change : 0 })
    }
    // "2026-10-20 15:08:01", İstanbul saati.
    const at = typeof j.Update_Date === 'string' ? j.Update_Date.replace(' ', 'T') + '+03:00' : new Date().toISOString()
    return { date: at, list, source: 'Piyasa' }
  } catch {
    return null
  }
}

async function fromCoinbase(): Promise<Rates | null> {
  try {
    const res = await fetch('https://api.coinbase.com/v2/exchange-rates?currency=TRY')
    if (!res.ok) return null
    const j = (await res.json()) as { data?: { rates?: Record<string, string> } }
    const r = j.data?.rates
    if (!r) return null
    const list: Rate[] = CODES.map((code) => ({ code, tl: 1 / Number(r[code]), changePct: 0 }))
    if (list.some((x) => !Number.isFinite(x.tl))) return null
    return { date: new Date().toISOString(), list, source: 'Coinbase' }
  } catch {
    return null
  }
}

/** WMO hava kodu → Türkçe ve simge türü. */
export function describeWeather(code: number): { text: string; kind: 'clear' | 'partly' | 'cloud' | 'fog' | 'rain' | 'snow' | 'storm' } {
  if (code === 0) return { text: 'Açık', kind: 'clear' }
  if (code === 1 || code === 2) return { text: 'Parçalı bulutlu', kind: 'partly' }
  if (code === 3) return { text: 'Bulutlu', kind: 'cloud' }
  if (code === 45 || code === 48) return { text: 'Sisli', kind: 'fog' }
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return { text: code >= 80 ? 'Sağanak' : 'Yağmurlu', kind: 'rain' }
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return { text: 'Karlı', kind: 'snow' }
  if (code >= 95) return { text: 'Gök gürültülü', kind: 'storm' }
  return { text: 'Bulutlu', kind: 'cloud' }
}
